import { useState } from 'react';
import { ActionIcon, Button, Center, Group, Loader, Menu, Paper, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import { IconPhone, IconTrash } from '@tabler/icons-react';
import { useMutation, useSubscription } from 'urql';
import { CRM_INTEGRATION_TYPES, isCrmIntegrationType } from '@shared';
import type { CrmIntegrationType } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { crmTypeLabels } from './crm-config';
import { PhoneCrmOverrideModal } from './phone-crm-override-modal';
import { RemovePhoneNumberCrmOverrideMutation, SystemOrganizationPhoneNumbersSubscription } from './graphql';
import type { SystemOrganizationPhoneNumber } from './graphql';

type CrmOverrideTarget = { phoneNumber: SystemOrganizationPhoneNumber; type: CrmIntegrationType };

const statusColors: Record<string, string> = {
  active: 'var(--mantine-color-green-6)',
  pending: 'var(--mantine-color-yellow-6)',
  released: 'var(--mantine-color-red-6)',
};

function PhoneNumberRow({
  phoneNumber,
  onEditOverride,
  onError,
}: {
  phoneNumber: SystemOrganizationPhoneNumber;
  onEditOverride: (target: CrmOverrideTarget) => void;
  onError: (message: string) => void;
}) {
  const [isRemoving, setIsRemoving] = useState(false);
  const [, runRemoveOverride] = useMutation(RemovePhoneNumberCrmOverrideMutation);
  const overrideId = phoneNumber.crmIntegrationId;
  const integrationType = phoneNumber.crmIntegration?.type;
  const overrideType: CrmIntegrationType = isCrmIntegrationType(integrationType) ? integrationType : 'caspio';

  const removeOverride = async (crmIntegrationId: string) => {
    setIsRemoving(true);

    const result = await runRemoveOverride({
      phoneNumberId: phoneNumber.id,
      crmIntegrationId,
      updatedAt: new Date().toISOString(),
    });

    setIsRemoving(false);

    if (result.error) onError(getErrorMessage(result.error, 'Failed to remove the CRM override'));
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" ff="monospace" fw={500}>
          {phoneNumber.phoneNumber}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {phoneNumber.friendlyName || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusColors[phoneNumber.status] ?? 'var(--mantine-color-gray-5)',
            }}
          />
          <Text size="sm" c="dimmed" tt="capitalize">
            {phoneNumber.status}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        {phoneNumber.crmIntegration ? (
          <Stack gap={0}>
            <Text size="sm" c="green.6">
              {phoneNumber.crmIntegration.name}
            </Text>
            <Text size="xs" c="dimmed">
              {crmTypeLabels[overrideType]}
            </Text>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            Inherits from org
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {overrideId ? (
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={() => onEditOverride({ phoneNumber, type: overrideType })}
            >
              Edit
            </Button>
          ) : (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Button variant="subtle" size="compact-sm">
                  Override
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {CRM_INTEGRATION_TYPES.map((type) => (
                  <Menu.Item key={type} onClick={() => onEditOverride({ phoneNumber, type })}>
                    {crmTypeLabels[type]}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
          {overrideId && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              loading={isRemoving}
              onClick={() => void removeOverride(overrideId)}
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function EmptyPhoneNumbers() {
  return (
    <Paper p="xl" withBorder radius="md">
      <Stack align="center" gap="md">
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconPhone size={24} />
        </ThemeIcon>
        <Stack gap={2} align="center">
          <Text fw={500}>No phone numbers</Text>
          <Text size="sm" c="dimmed">
            Phone numbers are assigned from the organization&apos;s dashboard.
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function PhoneNumbersPanel({ organizationId }: { organizationId: string }) {
  const [overrideTarget, setOverrideTarget] = useState<CrmOverrideTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [{ data, error: queryError }] = useSubscription({
    query: SystemOrganizationPhoneNumbersSubscription,
    variables: { organizationId },
  });

  if (queryError) return <ErrorAlert message={getErrorMessage(queryError, 'Unable to load phone numbers')} />;

  if (!data) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          Phone numbers assigned to this organization. Each number can optionally override the organization&apos;s CRM
          configuration.
        </Text>

        <ErrorAlert message={error} />

        {data.phoneNumbers.length === 0 ? (
          <EmptyPhoneNumbers />
        ) : (
          <Paper p={0} withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Phone Number</Table.Th>
                  <Table.Th>Friendly Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>CRM</Table.Th>
                  <Table.Th w={140} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.phoneNumbers.map((phoneNumber) => (
                  <PhoneNumberRow
                    key={phoneNumber.id}
                    phoneNumber={phoneNumber}
                    onEditOverride={setOverrideTarget}
                    onError={setError}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      {overrideTarget && (
        <PhoneCrmOverrideModal
          key={overrideTarget.phoneNumber.id}
          phoneNumber={overrideTarget.phoneNumber}
          type={overrideTarget.type}
          onClose={() => setOverrideTarget(null)}
        />
      )}
    </>
  );
}
